import React from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../common/views/SMLButton/SMLButton';
import { useIsNativeEmbed } from '../../music24/navigation/hooks/useIsNativeEmbed';
import { coverEntryMessages } from './coverEntryMessages';
import { offersCoverEditor, type CoverFieldState } from './coverFieldState';

interface CoverFieldActionsProps {
  state: CoverFieldState;
  /** Opens the cover editor of the song. */
  onDesign: () => void;
  /** Opens the file picker of the field. */
  onUpload: () => void;
  isUploading: boolean;
  disabled: boolean;
}

/**
 * The two actions of the shared cover field (design cover-editor a1, a2): the editor and a file.
 * The main action is the one that fits the cover the song has: no cover or a cover of the editor →
 * the editor; an uploaded or a store cover → a file. In a native app's web view the editor is not
 * offered (the apps keep their upload); only the file action shows.
 */
const CoverFieldActionsComponent: React.FC<CoverFieldActionsProps> = (props) => {
  const { state, onDesign, onUpload, isUploading, disabled } = props;
  const intl = useIntl();
  const hasEditor = offersCoverEditor({ isNativeEmbed: useIsNativeEmbed() });
  const editorLabel = {
    none: coverEntryMessages.design,
    editor: coverEntryMessages.edit,
    file: coverEntryMessages.designNew,
  }[state];
  const fileLabel = state === 'none' ? coverEntryMessages.upload : coverEntryMessages.replace;
  const editorIsMain = state !== 'file';

  const editorButton = hasEditor ? (
    <SMLButton size="sm" outline={!editorIsMain} omitCap onClick={onDesign} disabled={disabled}>
      {intl.formatMessage(editorLabel)}
    </SMLButton>
  ) : null;
  const fileButton = (
    <SMLButton
      size="sm"
      outline={editorIsMain && hasEditor}
      omitCap
      onClick={onUpload}
      isLoading={isUploading}
      disabled={disabled || isUploading}
    >
      {intl.formatMessage(fileLabel)}
    </SMLButton>
  );
  return editorIsMain ? (
    <>
      {editorButton}
      {fileButton}
    </>
  ) : (
    <>
      {fileButton}
      {editorButton}
    </>
  );
};

export const CoverFieldActions = TypedMemo(CoverFieldActionsComponent);
